import queryKeys from '@/constants/queryKeys';
import { organizationMembersForAdminGet } from '@/services/api/requests/organization';
import { TUser, TUserAffiliation } from '@/types/User.types';
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Header, HeaderRow, Body, Row, HeaderCell, Cell } from '@table-library/react-table-library/table';
import { useTheme } from '@table-library/react-table-library/theme';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppStore } from '@/store/useAppStore';
import CustomButton from '@/components/atoms/CustomButton';
import { FORM_FIELD_WIDTH, MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import OrganizationRosterDeleteMemberModal from './OrganizationRosterDeleteMemberModal';
import { TRosterItemWithIdAndRole } from '@/types/Roster.types';
import iconsPng from '@/assets/img/png/icons';
import tableTheme from '@/theme/components/table';
import { TABLE_COLUMNS_ORG_ROSTER_VIEW } from '@/utils/tableConfigs';
import roles from '@/constants/roles';
import useRemoveMemberFromOrganization from '@/hooks/organization/removeMemberFromOrganization';
import OrganizationRosterAddMemberModal from './OrganizationRosterAddMemberModal';
import OrgMemberPendingInvite from './OrgMemberPendingReinvite';
import CompactTablePagination from '@/components/molecules/CompactTablePagination/CompactTablePagination';
import OrganizationRosterPeerSupportModal from './OrganizationRosterPeerSupportModal';
import OrganizationRosterDowngradePeerSupportModal from './OrganizationRosterDowngradePeerSupportModal';
import useDowngradePeerSupportToMember from '@/hooks/peersupport/downgradePeerSupportToMember';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import useReinviteMemberToOrganization from '@/hooks/organization/reinviteMemberToOrganization';
import { usePaginatedSearch } from '@/hooks/usePaginatedSearch';
import SearchInputBox from '@/components/atoms/SearchInputBox';
import { MIN_CHARS_TO_DO_SEARCH, USER_AFFILIATION_OPTIONS } from '@/constants';
import { capitalize } from '@/utils/stringUtils';
import PeerSupportSwitch from './PeerSupportSwitch';
import svgIcons from '@/assets/svg/icons';
import colors from '@/theme/colors';
import OrganizationRosterUpdateAffiliationModal from './OrganizationRosterUpdateAffiliationModal';
import useUpdateUserAffiliation from '@/hooks/organization/updateUserAffiliation';
import LogoSpinner from '@/components/atoms/LogoSpinner';
import PeerSupportLeadIconTrigger from './PeerSupportLeadIconTrigger';
import OrgMembersRosterBulkAdd from './OrgMembersRosterBulkAdd';
import { revokeInvitationPost } from '@/services/api/requests/organization';
import { TCustomAxiosError } from '@/services/api';
import useToast from '@/hooks/useToast';
import {
  blockedActionMessages,
  blockedResolutionMessages,
  getRevokeRefusalReason,
  revokeRefusalMessages,
} from './invitationMessages';
import { resolveInvitationRevokePost } from '@/services/api/requests/organization';
import type { ResolveInvitationRevokeDto } from '@/types/generated/schemas';
import { useAuthContext } from '@/context/auth/useAuthContext';

const WAIT_LOADING_MILLISECONDS = 700;

const TOTAL_COLUMNS_WITHOUT_DELETE = TABLE_COLUMNS_ORG_ROSTER_VIEW.length;

const COLUMN_PERCENTAGES = '12% 12% 12% 25% 10% 12% 9% 8%';

const getReinviteBlockedReason = (reason?: NonNullable<TUser['blocked_actions']>[string]) =>
  reason ? blockedActionMessages[reason] : undefined;

const getAllowedResolutionDecisions = (user: TUser): ResolveInvitationRevokeDto['decision'][] =>
  (user.allowed_decisions ?? []).filter(
    (decision) =>
      !user.resolution ||
      decision === user.resolution.decision ||
      (user.resolution.state === 'BLOCKED' && decision === 'REMOVE'),
  );

const OrgMembersRoster = () => {
  const theme = useTheme({
    ...tableTheme,
    Table: `${tableTheme.Table}; --data-table-library_grid-template-columns: ${COLUMN_PERCENTAGES}`,
  });

  const queryClient = useQueryClient();
  const toast = useToast();
  const { isSuperAdmin } = useAuthContext();

  const { organization } = useAppStore();

  const [isBulkAdding, setIsBulkAdding] = useState(false);

  const [addMemberModalVisible, setAddMemberModalVisible] = useState(false);
  const [isReinviteModalOpened, setIsReinviteModalOpened] = useState(false);
  const [deleteConfirmationModalVisible, setDeleteConfirmationModalVisible] = useState(false);
  const [downgradeConfirmationModalVisible, setDowngradeConfirmationModalVisible] = useState(false);
  const [updateAffiliationConfirmationModalVisible, setUpdateAffiliationConfirmationModalVisible] = useState(false);

  const [itemToDeleteEmail, setItemToDeleteEmail] = useState('');
  const [itemToDeleteId, setItemToDeleteId] = useState('');

  const onClickDelete = useCallback((item: TRosterItemWithIdAndRole) => {
    setItemToDeleteId(item.id);
    setItemToDeleteEmail(item.Email);
    setDeleteConfirmationModalVisible(true);
  }, []);

  const [peerSupportModalVisible, setPeerSupportModalVisible] = useState(false);
  const [itemToUpgradeToPeerSupportId, setItemToUpgradeToPeerSupportId] = useState('');
  const [itemToUpgradeToPeerSupportName, setItemToUpgradeToPeerSupportName] = useState('second');

  const [itemToDowngradeFromPeerSupportName, setItemToDowngradeFromPeerSupportName] = useState('');
  const [itemToDowngradeFromPeerSupportId, setItemToDowngradeFromPeerSupportId] = useState('');

  const onClickPeerSupport = useCallback((item: TRosterItemWithIdAndRole) => {
    if (item.isPeerSupport) {
      setItemToDowngradeFromPeerSupportId(item.id);
      setItemToDowngradeFromPeerSupportName(`${item['First Name']} ${item['Last Name']}`);
      setDowngradeConfirmationModalVisible(true);
    } else {
      setItemToUpgradeToPeerSupportId(item.id);
      setItemToUpgradeToPeerSupportName(`${item['First Name']} ${item['Last Name']}`);
      setPeerSupportModalVisible(true);
    }
  }, []);

  const [itemToUpdateAffiliation, setItemToUpdateAffiliation] = useState<TRosterItemWithIdAndRole>();
  const [newAffiliation, setNewAffiliation] = useState<TUserAffiliation>();

  const onClickUpdateAffiliation = useCallback((item: TRosterItemWithIdAndRole, newAffiliation: TUserAffiliation) => {
    setItemToUpdateAffiliation(item);
    setNewAffiliation(newAffiliation);
    setUpdateAffiliationConfirmationModalVisible(true);
  }, []);

  const tableColumns = useMemo(() => {
    const columns: {
      label: string | React.JSX.Element;
      renderCell: (item: TRosterItemWithIdAndRole) => string | React.JSX.Element;
    }[] = TABLE_COLUMNS_ORG_ROSTER_VIEW;

    if (columns.length === TOTAL_COLUMNS_WITHOUT_DELETE) {
      columns.push({
        label: <Flex maxWidth={'100px'}></Flex>,
        renderCell: () => '',
      });
    }
    return columns;
  }, [onClickDelete]);

  const [filterText, setFilterText] = useState('');

  const {
    isLoading: isOrgMembersLoading,
    data: orgMembers,
    refetch: refetchOrgMembers,
    isRefetching: isFetchingOrgMembers,
    pagination,
  } = usePaginatedSearch<TUser[], string, string>({
    query: organizationMembersForAdminGet,
    queryKey: queryKeys.organization.members,
    searchFilterType: '',
    searchFilterText: filterText.length >= MIN_CHARS_TO_DO_SEARCH ? filterText : undefined,
  });

  const hasActiveResolution = orgMembers?.some(
    (user) => user.resolution && ['SANITIZING', 'REISSUING', 'REMOVING'].includes(user.resolution.state),
  );

  useEffect(() => {
    if (!hasActiveResolution) return;
    let cancelled = false;
    let delay = 3000;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.organization.members] });
      if (cancelled) return;
      delay = Math.min(Math.round(delay * 1.5), 30000);
      timer = setTimeout(poll, delay);
    };
    timer = setTimeout(poll, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [hasActiveResolution, queryClient]);

  const [isWaitLoading, setIsWaitLoading] = useState(false); // improves UX by having the UI start the loader immediately after an action (add/delete member); this is needed because we have to wait 2 seconds to fetch data from auth0

  const isLoading = isOrgMembersLoading || isWaitLoading;

  const [userNameToReinvite, setUserNameToReinvite] = useState('');
  const [userIdToReinvite, setUserIdToReinvite] = useState('');
  const [reinvitedIds, setReinvitedIds] = useState<Set<string>>(() => new Set());
  const [revokedIds, setRevokedIds] = useState<Set<string>>(() => new Set());
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; email: string; retry: boolean } | null>(null);
  const [resolutionTarget, setResolutionTarget] = useState<TUser | null>(null);
  const [resolutionDecision, setResolutionDecision] = useState<ResolveInvitationRevokeDto['decision'] | ''>('');
  const [resolutionReason, setResolutionReason] = useState('');

  const handleInvitationError = (error: TCustomAxiosError) => {
    const reason = getRevokeRefusalReason(error.reason);
    if (
      reason === 'ALREADY_ACCEPTED' ||
      reason === 'NEEDS_REPAIR' ||
      reason === 'REVOKE_UNFINISHED' ||
      reason === 'RESOLUTION_CONFLICT'
    ) {
      void queryClient.invalidateQueries({ queryKey: [queryKeys.organization.members] });
    }
    if (reason === 'RESOLUTION_CONFLICT' || reason === 'ALREADY_ACCEPTED') {
      setResolutionTarget(null);
    }
    if (reason === 'ACCOUNT_OPERATION_IN_PROGRESS' || reason === 'MAINTENANCE_FREEZE') {
      toast({ id: `invitation-${reason}` }, error);
    } else if (reason) {
      toast({
        id: `invitation-${reason}`,
        status: 'error',
        title: 'Invitation action was not completed',
        description: revokeRefusalMessages[reason],
      });
    } else {
      toast({ title: 'Could not update invitation' }, error);
    }
  };

  const { mutate: revokeInvitation, isPending: isRevokePending } = useMutation({
    mutationFn: (userId: string) => {
      if (!organization?._id) throw new Error('No Organization ID');
      return revokeInvitationPost(organization._id, userId);
    },
    onSuccess: (response, userId) => {
      if (response.outcome === 'REVOKED') {
        setRevokedIds((ids) => new Set(ids).add(userId));
        toast({ title: 'Invitation revoked', description: 'You can invite this email again.' });
      } else {
        toast({ title: 'Invitation resolved', description: 'The roster is updating.' });
      }
      void queryClient.invalidateQueries({ queryKey: [queryKeys.organization.members] });
    },
    onError: handleInvitationError,
  });

  const { mutate: resolveInvitation, isPending: isResolvePending } = useMutation({
    mutationFn: ({ userId, decision, reason }: { userId: string } & ResolveInvitationRevokeDto) => {
      if (!organization?._id) throw new Error('No Organization ID');
      return resolveInvitationRevokePost(organization._id, userId, { decision, reason });
    },
    onSuccess: (response, variables) => {
      if (
        response.outcome === 'REVOKED' ||
        (response.resolution?.decision === 'REMOVE' && response.resolution.state === 'DONE')
      ) {
        setRevokedIds((ids) => new Set(ids).add(variables.userId));
      }
      setResolutionTarget(null);
      setResolutionReason('');
      toast({ title: 'Resolution recorded', description: 'The roster is updating.' });
      void queryClient.invalidateQueries({ queryKey: [queryKeys.organization.members] });
    },
    onError: handleInvitationError,
  });

  const {
    mutate: reinviteMemberToOrganization,
    isPending: isMemberReinvitePending,
    variables: reinvitingUserId,
  } = useReinviteMemberToOrganization((userId) => {
    setReinvitedIds((ids) => new Set(ids).add(userId));
    void queryClient.invalidateQueries({ queryKey: [queryKeys.organization.members] });
  });

  const mapBeUserToTableUser = useCallback(
    (users: TUser[]): TRosterItemWithIdAndRole[] =>
      users
        .filter(
          (user) =>
            !revokedIds.has(user.user_id) &&
            !(user.resolution?.decision === 'REMOVE' && user.resolution.state === 'DONE'),
        )
        .map((user) => ({
          'First Name': user.firstName ?? user.user_metadata.firstName,
          'Last Name': user.lastName ?? user.user_metadata.lastName,
          Phone: user.user_metadata.phone ?? '/',
          Email: user.email,
          Affiliation: (user.userAffiliation?.toLowerCase() as TUserAffiliation) ?? 'None',
          'Peer Support': user.roles?.includes(roles.peerSupport),
          Invitation:
            (user.invitation_status === 'ACTIVE' && !user.revoke_state && !user.resolution) ||
            (user.resolution?.decision === 'ACCEPTED' && user.resolution.state === 'DONE') ? (
              'Accepted'
            ) : (
              <OrgMemberPendingInvite
                isPending={isMemberReinvitePending || isRevokePending || isResolvePending}
                isReinviting={isMemberReinvitePending && reinvitingUserId === user.user_id}
                status={
                  user.resolution?.state === 'BLOCKED'
                    ? `Blocked: ${user.resolution.blocked_reason ? blockedResolutionMessages[user.resolution.blocked_reason] : 'Support review is needed.'}`
                    : user.resolution && user.resolution.state !== 'DONE'
                      ? user.resolution.state.charAt(0) + user.resolution.state.slice(1).toLowerCase()
                      : user.resolution?.decision === 'SANITIZE' && user.resolution.state === 'DONE'
                        ? 'Pending'
                        : user.revoke_state === 'NEEDS_REPAIR'
                          ? 'Needs support'
                          : user.revoke_state === 'UNFINISHED'
                            ? 'Unfinished revoke'
                            : !reinvitedIds.has(user.user_id) &&
                                (user.invitation_link === 'EXPIRED' || user.invitation_link === 'UNUSABLE')
                              ? 'Expired'
                              : 'Pending'
                }
                reinviteBlockedReason={
                  !user.invitation_actions?.includes('REINVITE')
                    ? getReinviteBlockedReason(user.blocked_actions?.REINVITE)
                    : undefined
                }
                revokeBlockedReason={
                  !user.invitation_actions?.includes('REVOKE') && user.blocked_actions?.REVOKE
                    ? blockedActionMessages[user.blocked_actions.REVOKE]
                    : undefined
                }
                onReinvite={
                  user.revoke_state !== 'NEEDS_REPAIR' && user.invitation_actions?.includes('REINVITE')
                    ? () => {
                        setUserNameToReinvite(
                          `${user?.firstName ?? user?.user_metadata?.firstName} ${user?.lastName ?? user?.user_metadata.lastName}`,
                        );
                        setUserIdToReinvite(user.user_id);
                        setIsReinviteModalOpened(true);
                      }
                    : undefined
                }
                onResolve={
                  isSuperAdmin &&
                  (user.revoke_state === 'NEEDS_REPAIR' || !!user.resolution) &&
                  getAllowedResolutionDecisions(user).length > 0
                    ? () => {
                        setResolutionTarget(user);
                        setResolutionDecision(getAllowedResolutionDecisions(user)[0]);
                        setResolutionReason('');
                      }
                    : undefined
                }
                resolutionNote={
                  user.resolution ? `Recorded decision: ${user.resolution.decision.toLowerCase()}` : undefined
                }
              />
            ),
          id: user.user_id,
          invitationStatus: user.invitation_status,
          needsRepair: user.revoke_state === 'NEEDS_REPAIR',
          onRevoke:
            user.invitation_status !== 'ACTIVE' &&
            user.revoke_state !== 'NEEDS_REPAIR' &&
            user.invitation_actions?.includes('REVOKE')
              ? () =>
                  setRevokeTarget({
                    id: user.user_id,
                    email: user.email,
                    retry: user.revoke_state === 'UNFINISHED',
                  })
              : undefined,
          revokeLabel: user.revoke_state === 'UNFINISHED' ? 'Retry revoke' : 'Revoke',
          revokeDisabled: isMemberReinvitePending || isRevokePending || isResolvePending,
          isAdmin: user.roles?.includes(roles.orgAdmin),
          isPeerSupport: user.roles?.includes(roles.peerSupport),
          'Peer Support Lead': user.roles?.includes(roles.peerSupportLead),
        })),
    [isMemberReinvitePending, reinvitingUserId, isRevokePending, isResolvePending, reinvitedIds, revokedIds, isSuperAdmin],
  );

  const [rawData, setRawData] = useState([] as TRosterItemWithIdAndRole[]);

  useEffect(() => {
    if (orgMembers) {
      setRawData(mapBeUserToTableUser(orgMembers));
      setReinvitedIds((ids) => {
        const confirmedIds = orgMembers
          .filter((user) => user.invitation_link === 'USABLE' || user.invitation_status === 'ACTIVE')
          .map((user) => user.user_id);
        if (!confirmedIds.some((id) => ids.has(id))) return ids;
        const remaining = new Set(ids);
        confirmedIds.forEach((id) => remaining.delete(id));
        return remaining;
      });
    }
  }, [orgMembers, mapBeUserToTableUser]);

  const data = useMemo(() => ({ nodes: rawData ?? [] }), [rawData]);

  const reFetch = useCallback(() => {
    // TODO: see how to improve this... remove the wishful 2s wait and waitLoading...
    setTimeout(() => setIsWaitLoading(true), WAIT_LOADING_MILLISECONDS);
    setTimeout(() => {
      pagination.getFirstPage();
      queryClient.invalidateQueries({ queryKey: [queryKeys.organization.members], exact: false });
      setIsWaitLoading(false);
    }, 2000); // if we don't wait 2 seconds before re-fetching, newly added user doesn't appear
  }, [refetchOrgMembers]);

  const { mutate: removeOrgMember } = useRemoveMemberFromOrganization(reFetch);

  const deleteMember = useCallback(
    (memberId: string) => {
      setTimeout(() => setIsWaitLoading(true), WAIT_LOADING_MILLISECONDS);
      removeOrgMember(memberId);
    },
    [removeOrgMember],
  );

  const { mutate: downgradePeerSupportToMemberMutate, isPending: isDowngradePending } = useDowngradePeerSupportToMember(
    () => setDowngradeConfirmationModalVisible(false),
  );

  const downgradePeerSupportToMember = useCallback(() => {
    downgradePeerSupportToMemberMutate({
      organizationId: organization?._id ?? '',
      userId: itemToDowngradeFromPeerSupportId,
    });
  }, [organization, itemToDowngradeFromPeerSupportId]);

  const { mutate: updateUserAffiliation, isPending: isUpdatePending } = useUpdateUserAffiliation(
    organization?._id ?? '',
    () => {
      setUpdateAffiliationConfirmationModalVisible(false);
      reFetch();
    },
  );

  const onClickUpdateUserAffiliation = useCallback(() => {
    setTimeout(() => setIsWaitLoading(true), WAIT_LOADING_MILLISECONDS);
    updateUserAffiliation({ userId: itemToUpdateAffiliation?.id ?? '', newAffiliation: newAffiliation! });
  }, [itemToUpdateAffiliation, newAffiliation]);

  const [rowHoveredId, setRowHoveredId] = useState('');

  if (isBulkAdding) {
    return (
      <OrgMembersRosterBulkAdd
        onMemberAddFinish={() => {
          setIsBulkAdding(false);
          reFetch();
        }}
        onGoBack={() => setIsBulkAdding(false)}
      />
    );
  }

  return (
    <Center marginTop={'30px'} paddingBottom={'10vh'}>
      <VStack width={MAX_CONTAINER_WIDTH} gap={0}>
        <HStack width={'100%'} justifyContent={'space-between'} alignItems={'center'}>
          <Text variant={'loraTitle'}>{organization?.name}&apos;s Roster</Text>

          <HStack gap={'30px'}>
            <SearchInputBox setFilterText={setFilterText} isSearching={isFetchingOrgMembers} />

            <CustomButton
              label="Bulk Add via CSV"
              isTransparent
              labelColor="text.mediumBlue"
              labelHoverColor="text.darkBlue"
              onClick={() => setIsBulkAdding(true)}
            />

            <CustomButton
              width={'150px'}
              height={'55px'}
              onClick={() => setAddMemberModalVisible(true)}
              backgroundColor="primary.500"
              labelColor="extra.white"
              label="Add Member"
            />
          </HStack>
        </HStack>

        {isLoading ? (
          <Center height={'860px'} paddingBottom={'20vh'}>
            <LogoSpinner />
          </Center>
        ) : (
          <VStack width={'100%'}>
            <Box marginTop={'60px'} width={'100%'}>
              {data.nodes.length > 0 ? (
                <>
                  <Table data={data} theme={theme} layout={{ custom: true }}>
                    {(tableDataItems: TRosterItemWithIdAndRole[]) => (
                      <>
                        <Header>
                          <HeaderRow>
                            {tableColumns.map((column) => (
                              <HeaderCell key={column.label.toString()}>{column.label}</HeaderCell>
                            ))}
                          </HeaderRow>
                        </Header>

                        <Body>
                          {tableDataItems.map((item, index) => {
                            return (
                              <Row
                                key={item.id}
                                item={item}
                                className={`tr-body ${index < tableDataItems.length - 1 ? 'border-bottom-items-container' : ''}`}
                                onMouseEnter={() => setRowHoveredId(item.id)}
                                onMouseLeave={() => setRowHoveredId('')}
                              >
                                <Cell>{item['First Name']}</Cell>
                                <Cell>{item['Last Name']}</Cell>
                                <Cell>{item.Phone}</Cell>
                                <Cell>{item.Email}</Cell>
                                <Cell>
                                  {item.isAdmin || item.needsRepair ? (
                                    <Text variant={'urbanistSemiBold'} textAlign={'start'}>
                                      {item.isAdmin ? 'Admin' : capitalize(item.Affiliation)}
                                    </Text>
                                  ) : (
                                    <Select
                                      focusBorderColor="transparent"
                                      className="custom-input custom-select-padding"
                                      value={item.Affiliation}
                                      style={{
                                        fontSize: '16px',
                                        width: 150,
                                        marginLeft: '-15px',
                                        color: colors.text.mediumBlue,
                                        border: 0,
                                        cursor: 'pointer',
                                      }}
                                      _hover={{ opacity: 0.7 }}
                                      onChange={(event) =>
                                        onClickUpdateAffiliation(item, event.target.value as TUserAffiliation)
                                      }
                                    >
                                      {USER_AFFILIATION_OPTIONS.map((affiliationOption) => (
                                        <option key={affiliationOption} value={affiliationOption}>
                                          {capitalize(affiliationOption)}
                                        </option>
                                      ))}
                                    </Select>
                                  )}
                                </Cell>
                                <Cell>{item.Invitation}</Cell>
                                <Cell>
                                  {!item.needsRepair && (
                                    <HStack gap={'10px'}>
                                      <PeerSupportSwitch
                                        checked={item['Peer Support']}
                                        onChange={() => onClickPeerSupport(item)}
                                      />

                                      <PeerSupportLeadIconTrigger
                                        isPeerSupportLead={!!item['Peer Support Lead']}
                                        isPeerSupport={item['Peer Support']}
                                        isOrgAdmin={item.isAdmin}
                                        userId={item.id}
                                        isRowHovered={rowHoveredId === item.id}
                                        userFullName={`${item['First Name']} ${item['Last Name']}`}
                                      />
                                    </HStack>
                                  )}
                                </Cell>
                                {/* Removing an accepted member and revoking an invitation are separate actions. */}
                                <Cell>
                                  {item.onRevoke && (
                                    <Tooltip
                                      label={item.revokeLabel}
                                      placement={'right-start'}
                                      hasArrow
                                      color={'text.mediumBlue'}
                                      background={'white'}
                                      maxWidth={FORM_FIELD_WIDTH}
                                      borderRadius={'8px'}
                                      fontWeight={600}
                                      padding={'10px'}
                                      left={-5}
                                    >
                                      <VStack paddingY={'10px'}>
                                        <CustomButton
                                          ariaLabel={item.revokeLabel}
                                          hoverOpacityDrop
                                          onClick={item.onRevoke}
                                          iconBeforeLabelSvgSize={16}
                                          iconBeforeLabelSvgBoxHeight={14}
                                          iconBeforeLabelSvgBoxWidth={12}
                                          iconBeforeLabelSvgPath={svgIcons.trashCan}
                                          iconBeforeLabelSvgColor={colors.extra.red}
                                          width={'30px'}
                                          height={'30px'}
                                          disabledColor="#fff"
                                          labelStyle={{ marginLeft: 0, marginTop: '6px' }}
                                          style={{ minWidth: '30px', borderRadius: '50%', paddingInline: 0 }}
                                          disabled={item.revokeDisabled}
                                        />
                                      </VStack>
                                    </Tooltip>
                                  )}
                                  {item.invitationStatus === 'ACTIVE' && !item.needsRepair && (
                                    <Tooltip
                                      label={item.isAdmin ? 'Admins cannot be deleted' : 'Delete'}
                                      placement={'right-start'}
                                      hasArrow
                                      color={'text.mediumBlue'}
                                      background={'white'}
                                      maxWidth={FORM_FIELD_WIDTH}
                                      borderRadius={'8px'}
                                      fontWeight={600}
                                      padding={'10px'}
                                      zIndex={20}
                                      left={-5}
                                    >
                                      <VStack paddingY={'10px'}>
                                        <CustomButton
                                          ariaLabel={item.isAdmin ? 'Admin cannot be deleted' : 'Delete user'}
                                          hoverOpacityDrop
                                          onClick={() => onClickDelete(item)}
                                          iconBeforeLabelSvgSize={16}
                                          iconBeforeLabelSvgBoxHeight={14}
                                          iconBeforeLabelSvgBoxWidth={12}
                                          iconBeforeLabelSvgPath={svgIcons.trashCan}
                                          iconBeforeLabelSvgColor={colors.extra.red}
                                          // hoverBackgroundColor={colors.extra.lightBlue}
                                          width={'30px'}
                                          height={'30px'}
                                          disabledColor="#fff"
                                          labelStyle={{ marginLeft: 0, marginTop: '6px' }}
                                          style={{ minWidth: '30px', borderRadius: '50%', paddingInline: 0 }}
                                          disabled={item.isAdmin}
                                        />
                                      </VStack>
                                    </Tooltip>
                                  )}
                                </Cell>
                              </Row>
                            );
                          })}
                        </Body>
                      </>
                    )}
                  </Table>
                  <CompactTablePagination pagination={pagination} isFetchingNextPage={isFetchingOrgMembers} />
                </>
              ) : (
                <VStack
                  paddingY={'60px'}
                  justifyContent={'center'}
                  backgroundColor={'extra.white'}
                  borderRadius={'10px'}
                  width={MAX_CONTAINER_WIDTH}
                >
                  <Image alt="notepad" src={iconsPng.notepad} width={120} height={120} />
                  <Text variant={'urbanistSemiBoldSmallTitle'} color={'text.mediumGray'} marginTop={'30px'}>
                    {filterText.length >= MIN_CHARS_TO_DO_SEARCH
                      ? 'No members found.'
                      : 'Start by inviting/adding members to roster.'}
                  </Text>
                </VStack>
              )}
            </Box>
          </VStack>
        )}

        {/* Add Member modal */}
        <OrganizationRosterAddMemberModal
          addMemberModalVisible={addMemberModalVisible}
          setAddMemberModalVisible={setAddMemberModalVisible}
          reFetch={reFetch}
        />

        {/* Reinvite Member modal */}
        <ConfirmationModal
          title="Confirm Reinvite"
          description={`Send another invite to ${userNameToReinvite}?`}
          confirmButtonText="Reinvite"
          isOpen={isReinviteModalOpened}
          onClose={() => setIsReinviteModalOpened(false)}
          onConfirm={() => {
            reinviteMemberToOrganization(userIdToReinvite);
            setIsReinviteModalOpened(false);
          }}
        />

        <ConfirmationModal
          title={revokeTarget?.retry ? 'Retry revoke' : 'Revoke invitation'}
          description={`Withdraw the invitation for ${revokeTarget?.email ?? ''}? This removes the never-accepted account.`}
          confirmButtonText={revokeTarget?.retry ? 'Retry revoke' : 'Revoke invitation'}
          confirmButtonColor="extra.red"
          isOpen={!!revokeTarget}
          onClose={() => setRevokeTarget(null)}
          confirmLoading={isRevokePending}
          onConfirm={() => {
            if (!revokeTarget) return;
            revokeInvitation(revokeTarget.id);
            setRevokeTarget(null);
          }}
        />

        <Modal isOpen={!!resolutionTarget} onClose={() => setResolutionTarget(null)} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Resolve invitation revoke</ModalHeader>
            <ModalBody>
              <Text mb={3}>Record a decision for {resolutionTarget?.email}.</Text>
              <Select
                aria-label="Resolution decision"
                value={resolutionDecision}
                onChange={(event) =>
                  setResolutionDecision(event.target.value as ResolveInvitationRevokeDto['decision'])
                }
              >
                {resolutionTarget &&
                  getAllowedResolutionDecisions(resolutionTarget).map((decision) => (
                    <option key={decision} value={decision}>
                      {decision}
                    </option>
                  ))}
              </Select>
              <Textarea
                aria-label="Reason for resolution"
                placeholder="Reason for this decision"
                value={resolutionReason}
                onChange={(event) => setResolutionReason(event.target.value)}
                mt={3}
              />
              <Text fontSize="sm" mt={2}>
                This decision is recorded. Repeating it resumes the same resolution.
              </Text>
            </ModalBody>
            <ModalFooter gap={2}>
              <Button variant="ghost" onClick={() => setResolutionTarget(null)}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                isLoading={isResolvePending}
                isDisabled={!resolutionDecision || !resolutionReason.trim()}
                onClick={() => {
                  if (!resolutionTarget || !resolutionDecision || !resolutionReason.trim()) return;
                  resolveInvitation({
                    userId: resolutionTarget.user_id,
                    decision: resolutionDecision,
                    reason: resolutionReason.trim(),
                  });
                }}
              >
                Confirm resolution
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <OrganizationRosterDeleteMemberModal
          deleteConfirmationModalVisible={deleteConfirmationModalVisible}
          setDeleteConfirmationModalVisible={setDeleteConfirmationModalVisible}
          deleteItem={deleteMember}
          itemToDeleteId={itemToDeleteId}
          itemToDeleteEmail={itemToDeleteEmail}
        />

        <OrganizationRosterPeerSupportModal
          peerSupportModalVisible={peerSupportModalVisible}
          setPeerSupportModalVisible={setPeerSupportModalVisible}
          organizationId={organization?._id ?? ''}
          userToUpgradeToPeerSupportId={itemToUpgradeToPeerSupportId}
          itemToUpgradeToPeerSupportName={itemToUpgradeToPeerSupportName}
        />

        <OrganizationRosterDowngradePeerSupportModal
          downgradeConfirmationModalVisible={downgradeConfirmationModalVisible}
          setDowngradeConfirmationModalVisible={setDowngradeConfirmationModalVisible}
          downgradeItem={downgradePeerSupportToMember}
          itemToDowngradeName={itemToDowngradeFromPeerSupportName}
          itemToDowngradeId={itemToDowngradeFromPeerSupportId}
          isDowngradePending={isDowngradePending}
        />

        <OrganizationRosterUpdateAffiliationModal
          confirmationModalVisible={updateAffiliationConfirmationModalVisible}
          setConfirmationModalVisible={setUpdateAffiliationConfirmationModalVisible}
          updateItemAffiliation={onClickUpdateUserAffiliation}
          itemToUpdate={itemToUpdateAffiliation}
          newAffiliation={newAffiliation}
          isUpdatePending={isUpdatePending}
        />
      </VStack>
    </Center>
  );
};

export default OrgMembersRoster;
